import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Bot, Lock, Send, X } from 'lucide-react';

import {
  addTicketNote,
  applyMacroToTicket,
  assignTicket,
  escalateTicket,
  getTicket,
  replyToTicket,
  setTicketPriority,
  setTicketStatus,
  setTicketTags,
  CS_TICKET_PRIORITIES,
  CS_TICKET_STATUSES,
  type CsTicketDetail,
  type CsTicketMessage,
  type CsTicketPriority,
  type CsTicketStatus,
} from '@/api/cs/tickets.api';
import { listCsAgents } from '@/api/cs/agents.api';
import { listCsGroups, listCsTags } from '@/api/cs/routing.api';
import { listCsMacros } from '@/api/cs/workspace.api';
import { TagChip, TierBadge } from '@/components/cs/badges';
import { PRIORITY_META, STATUS_META } from '@/components/cs/meta';
import { CsAccessDenied, CsErrorCard, CsLoading, SELECT_CLS, TEXTAREA_CLS } from '@/components/cs/PageStates';
import { WidgetSlotCard } from '@/components/cs/WidgetSlotCard';
import { describeCsError } from '@/components/cs/csError';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/cn';
import { hasCapability } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * CS ticket detail — /cs/tickets/:ticketId (Phase 16 / ADR-026).
 *
 * Left: conversation thread (customer / agent / internal-note styling,
 * "[AI Draft]" notes badged) + composer with a Reply / Internal-note
 * toggle and a macro picker. Header: subject + customer + editors for
 * status/priority/assignee/group/tags. Right rail: triage decision,
 * assignment history, escalation, and the three CS-Phase-5 widget
 * SLOTS (deliberate structural stubs, spec §3.7).
 */

/** Server-side prefix on tier-2 draft notes (triage/apply.ts). */
const AI_DRAFT_PREFIX = '[AI Draft]';

export function CsTicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const canUseCs = me !== null && hasCapability(me, 'use_cs');

  const ticketQuery = useQuery({
    queryKey: ['cs', 'ticket', ticketId],
    queryFn: () => getTicket(ticketId ?? ''),
    enabled: canUseCs && Boolean(ticketId),
  });
  const agentsQuery = useQuery({ queryKey: ['cs', 'agents'], queryFn: listCsAgents, enabled: canUseCs });
  const groupsQuery = useQuery({ queryKey: ['cs', 'groups'], queryFn: listCsGroups, enabled: canUseCs });
  const tagsQuery = useQuery({ queryKey: ['cs', 'tags'], queryFn: listCsTags, enabled: canUseCs });
  const macrosQuery = useQuery({ queryKey: ['cs', 'macros'], queryFn: listCsMacros, enabled: canUseCs });

  const [escalateOpen, setEscalateOpen] = useState(false);

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['cs', 'ticket', ticketId] });
    void queryClient.invalidateQueries({ queryKey: ['cs', 'tickets'] });
  }

  function mutationToast(err: unknown, fallback: string): void {
    toast.show(describeCsError(err, fallback), { variant: 'danger' });
  }

  const statusMutation = useMutation({
    mutationFn: (status: CsTicketStatus) => setTicketStatus(ticketId ?? '', status),
    onSuccess: invalidate,
    onError: (err) => {
      mutationToast(err, 'Could not change the status.');
    },
  });
  const priorityMutation = useMutation({
    mutationFn: (priority: CsTicketPriority) => setTicketPriority(ticketId ?? '', priority),
    onSuccess: invalidate,
    onError: (err) => {
      mutationToast(err, 'Could not change the priority.');
    },
  });
  const assignMutation = useMutation({
    mutationFn: (input: { assigneeMembershipId?: string | null; groupId?: string | null }) =>
      assignTicket(ticketId ?? '', input),
    onSuccess: invalidate,
    onError: (err) => {
      mutationToast(err, 'Could not reassign the ticket.');
    },
  });
  const tagsMutation = useMutation({
    mutationFn: (tags: string[]) => setTicketTags(ticketId ?? '', tags),
    onSuccess: invalidate,
    onError: (err) => {
      mutationToast(err, 'Could not update the tags.');
    },
  });
  const escalateMutation = useMutation({
    mutationFn: (input: { toGroupId: string; reason: string; notes?: string }) =>
      escalateTicket(ticketId ?? '', input),
    onSuccess: () => {
      setEscalateOpen(false);
      invalidate();
      toast.show('Ticket escalated.', { variant: 'success' });
    },
    onError: (err) => {
      mutationToast(err, 'Could not escalate the ticket.');
    },
  });

  if (!canUseCs) {
    return <CsAccessDenied label="The CS workspace needs the use_cs capability." />;
  }
  if (ticketQuery.isLoading) return <CsLoading />;
  if (ticketQuery.error || !ticketQuery.data) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <BackLink />
        <CsErrorCard
          title="Couldn't load the ticket"
          error={ticketQuery.error}
          fallback="Ticket not found."
        />
      </div>
    );
  }

  const ticket = ticketQuery.data;
  const activeTags = (tagsQuery.data ?? []).filter((t) => t.active);
  const fullName = [ticket.csCustomer.firstName, ticket.csCustomer.lastName].filter(Boolean).join(' ');
  const customerName = fullName !== '' ? fullName : (ticket.csCustomer.email ?? 'Unknown customer');

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <BackLink />

      {/* ── Header ─────────────────────────────────────────────────── */}
      <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="typo-h2 break-words">{ticket.subject}</h1>
            <p className="mt-1 text-[13px] text-kw-muted">
              {customerName}
              {ticket.csCustomer.email ? ` · ${ticket.csCustomer.email}` : ''}
              {ticket.customerOrg ? ` · ${ticket.customerOrg.name}` : ''}
              {ticket.csCustomer.isPractitioner ? ' · practitioner' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <TierBadge tier={ticket.tier} />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setEscalateOpen(true);
              }}
            >
              <ArrowUpRight className="size-4" />
              Escalate
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-[12px] text-kw-muted">
            Status
            <select
              aria-label="Ticket status"
              value={ticket.status}
              disabled={statusMutation.isPending}
              onChange={(e) => {
                statusMutation.mutate(e.target.value as CsTicketStatus);
              }}
              className={SELECT_CLS}
            >
              {CS_TICKET_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_META[s].label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-[12px] text-kw-muted">
            Priority
            <select
              aria-label="Ticket priority"
              value={ticket.priority}
              disabled={priorityMutation.isPending}
              onChange={(e) => {
                priorityMutation.mutate(e.target.value as CsTicketPriority);
              }}
              className={SELECT_CLS}
            >
              {CS_TICKET_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_META[p].label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-[12px] text-kw-muted">
            Assignee
            <select
              aria-label="Ticket assignee"
              value={ticket.assignedMembershipId ?? ''}
              disabled={assignMutation.isPending}
              onChange={(e) => {
                assignMutation.mutate({
                  assigneeMembershipId: e.target.value === '' ? null : e.target.value,
                });
              }}
              className={SELECT_CLS}
            >
              <option value="">Unassigned</option>
              {(agentsQuery.data ?? []).map((a) => (
                <option key={a.id} value={a.membershipId}>
                  {a.membership.user.firstName} {a.membership.user.lastName}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-[12px] text-kw-muted">
            Group
            <select
              aria-label="Ticket group"
              value={ticket.groupId ?? ''}
              disabled={assignMutation.isPending}
              onChange={(e) => {
                assignMutation.mutate({ groupId: e.target.value === '' ? null : e.target.value });
              }}
              className={SELECT_CLS}
            >
              <option value="">No group</option>
              {(groupsQuery.data ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {ticket.tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-kw-full bg-kw-bg px-2 py-0.5 text-[11px] font-medium text-kw-shark"
            >
              {t}
              <button
                type="button"
                aria-label={`Remove tag ${t}`}
                disabled={tagsMutation.isPending}
                onClick={() => {
                  tagsMutation.mutate(ticket.tags.filter((x) => x !== t));
                }}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
          <select
            aria-label="Add tag"
            value=""
            disabled={tagsMutation.isPending}
            onChange={(e) => {
              const tag = e.target.value;
              if (tag && !ticket.tags.includes(tag)) tagsMutation.mutate([...ticket.tags, tag]);
            }}
            className={SELECT_CLS}
          >
            <option value="">Add tag…</option>
            {activeTags
              .filter((t) => !ticket.tags.includes(t.name))
              .map((t) => (
                <option key={t.id} value={t.name}>
                  {t.name}
                </option>
              ))}
          </select>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* ── Thread + composer ────────────────────────────────────── */}
        <div className="space-y-4">
          <Card className="p-0">
            {ticket.messages.length === 0 ? (
              <p className="px-4 py-8 text-center text-[13px] text-kw-muted">No messages yet.</p>
            ) : (
              <ul className="divide-y divide-kw-border">
                {ticket.messages.map((m) => (
                  <MessageRow key={m.id} message={m} customerName={customerName} />
                ))}
              </ul>
            )}
          </Card>

          <Composer
            ticketId={ticket.id}
            macros={(macrosQuery.data ?? []).filter((m) => m.isActive)}
            onDone={invalidate}
          />
        </div>

        {/* ── Right rail ───────────────────────────────────────────── */}
        <div className="space-y-3">
          {ticket.triageDecision ? <TriageCard ticket={ticket} /> : null}

          <Card className="p-4">
            <h2 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">
              Assignment history
            </h2>
            {ticket.assignmentEvents.length === 0 ? (
              <p className="mt-2 text-[12px] text-kw-muted">No assignment changes yet.</p>
            ) : (
              <ul className="mt-2 space-y-2">
                {ticket.assignmentEvents.map((e) => (
                  <li key={e.id} className="text-[12px] text-kw-shark">
                    <span className="font-medium">
                      {e.actorMembership
                        ? `${e.actorMembership.user.firstName} ${e.actorMembership.user.lastName}`
                        : 'System'}
                    </span>{' '}
                    <span className="text-kw-muted">
                      ({e.source}) · {new Date(e.createdAt).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {ticket.escalations.length > 0 ? (
            <Card className="p-4">
              <h2 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Escalations</h2>
              <ul className="mt-2 space-y-2">
                {ticket.escalations.map((e) => (
                  <li key={e.id} className="text-[12px] text-kw-shark">
                    <p className="font-medium">{e.reason}</p>
                    <p className="text-kw-muted">{new Date(e.createdAt).toLocaleString()}</p>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          {/* Deliberate CS-Phase-5 widget slots — spec §3.7. */}
          <WidgetSlotCard title="Order lookup" />
          <WidgetSlotCard title="Shipment tracking" />
          <WidgetSlotCard title="Kaerwell Connect" />
        </div>
      </div>

      <EscalateModal
        open={escalateOpen}
        groups={(groupsQuery.data ?? []).filter((g) => g.active && g.id !== ticket.groupId)}
        isPending={escalateMutation.isPending}
        onClose={() => {
          setEscalateOpen(false);
        }}
        onSubmit={(input) => {
          escalateMutation.mutate(input);
        }}
      />
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/cs" className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark">
      <ArrowLeft className="size-4" />
      Back to queue
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Thread message
// ─────────────────────────────────────────────────────────────────────────

function MessageRow({ message, customerName }: { message: CsTicketMessage; customerName: string }) {
  const isAiDraft = message.isPrivate && message.content.startsWith(AI_DRAFT_PREFIX);
  const senderName =
    message.senderType === 'customer'
      ? customerName
      : message.senderMembership
        ? `${message.senderMembership.user.firstName} ${message.senderMembership.user.lastName}`
        : 'System';

  return (
    <li
      className={cn(
        'px-4 py-3',
        message.isPrivate
          ? 'border-l-2 border-kw-warning-text/60 bg-kw-warning-bg/40'
          : message.senderType === 'customer'
            ? 'bg-white'
            : 'bg-kw-cerulean-light/30',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-medium text-kw-shark">{senderName}</span>
        {message.isPrivate ? (
          <span className="inline-flex items-center gap-1 rounded-kw-full bg-kw-warning-bg px-2 py-0.5 text-[11px] font-medium text-kw-warning-text">
            <Lock className="size-3" />
            Internal note
          </span>
        ) : null}
        {isAiDraft ? (
          <span className="inline-flex items-center gap-1 rounded-kw-full bg-kw-cerulean-light px-2 py-0.5 text-[11px] font-medium text-kw-cerulean-dark">
            <Bot className="size-3" />
            AI Draft
          </span>
        ) : null}
        {message.isAutoReply ? <Badge variant="info">Auto-reply</Badge> : null}
        <span className="ml-auto text-[11px] text-kw-muted">
          {new Date(message.createdAt).toLocaleString()}
        </span>
      </div>
      <p className="mt-1 whitespace-pre-wrap text-[13px] text-kw-shark">{message.content}</p>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Composer (reply / internal note + macro picker)
// ─────────────────────────────────────────────────────────────────────────

function Composer({
  ticketId,
  macros,
  onDone,
}: {
  ticketId: string;
  macros: { id: string; shortCode: string; title: string }[];
  onDone: () => void;
}) {
  const toast = useToast();
  const [mode, setMode] = useState<'reply' | 'note'>('reply');
  const [content, setContent] = useState('');
  const [macroId, setMacroId] = useState('');

  const sendMutation = useMutation({
    mutationFn: () =>
      mode === 'reply' ? replyToTicket(ticketId, content.trim()) : addTicketNote(ticketId, content.trim()),
    onSuccess: () => {
      setContent('');
      onDone();
      toast.show(mode === 'reply' ? 'Reply sent.' : 'Note added.', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeCsError(err, 'Could not send the message.'), { variant: 'danger' });
    },
  });

  const macroMutation = useMutation({
    mutationFn: () => applyMacroToTicket(ticketId, macroId),
    onSuccess: (result) => {
      setMacroId('');
      onDone();
      toast.show(`Macro applied (${result.applied.join(', ') || 'no steps'}).`, { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeCsError(err, 'Could not apply the macro.'), { variant: 'danger' });
    },
  });

  return (
    <Card className="space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-kw-md border border-kw-border p-0.5">
          <button
            type="button"
            aria-pressed={mode === 'reply'}
            onClick={() => {
              setMode('reply');
            }}
            className={cn(
              'rounded-kw-md px-3 py-1 text-[13px] font-medium',
              mode === 'reply' ? 'bg-kw-cerulean text-white' : 'text-kw-muted hover:text-kw-shark',
            )}
          >
            Reply
          </button>
          <button
            type="button"
            aria-pressed={mode === 'note'}
            onClick={() => {
              setMode('note');
            }}
            className={cn(
              'rounded-kw-md px-3 py-1 text-[13px] font-medium',
              mode === 'note' ? 'bg-kw-warning-text text-white' : 'text-kw-muted hover:text-kw-shark',
            )}
          >
            Internal note
          </button>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <select
            aria-label="Macro picker"
            value={macroId}
            onChange={(e) => {
              setMacroId(e.target.value);
            }}
            className={SELECT_CLS}
          >
            <option value="">Apply macro…</option>
            {macros.map((m) => (
              <option key={m.id} value={m.id}>
                {m.shortCode} — {m.title}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="secondary"
            disabled={macroId === ''}
            isLoading={macroMutation.isPending}
            onClick={() => {
              macroMutation.mutate();
            }}
          >
            Apply
          </Button>
        </div>
      </div>

      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          if (!content.trim()) return;
          sendMutation.mutate();
        }}
        className="space-y-3"
      >
        <textarea
          aria-label={mode === 'reply' ? 'Reply to customer' : 'Internal note'}
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
          }}
          rows={4}
          placeholder={
            mode === 'reply' ? 'Write a public reply to the customer…' : 'Write a private internal note…'
          }
          className={cn(TEXTAREA_CLS, mode === 'note' && 'border-kw-warning-text/50 bg-kw-warning-bg/30')}
        />
        <div className="flex justify-end">
          <Button type="submit" isLoading={sendMutation.isPending} disabled={!content.trim()}>
            <Send className="size-4" />
            {mode === 'reply' ? 'Send reply' : 'Add note'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Triage card
// ─────────────────────────────────────────────────────────────────────────

function TriageCard({ ticket }: { ticket: CsTicketDetail }) {
  const decision = ticket.triageDecision;
  if (!decision) return null;
  return (
    <Card className="space-y-2 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">AI triage</h2>
        <TierBadge tier={decision.assignedTier} />
      </div>
      <p className="text-[12px] text-kw-muted">
        Confidence {Math.round(decision.confidence * 100)}%
        {decision.tierDowngraded ? ' · downgraded by threshold' : ''}
        {decision.wasOverridden ? ' · overridden' : ''}
      </p>
      <p className="whitespace-pre-wrap text-[13px] text-kw-shark">{decision.reasoning}</p>
      {decision.suggestedTags.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {decision.suggestedTags.map((t) => (
            <TagChip key={t} tag={t} />
          ))}
        </div>
      ) : null}
      <p className="text-[11px] text-kw-ghost">{decision.model}</p>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Escalation modal
// ─────────────────────────────────────────────────────────────────────────

function EscalateModal({
  open,
  groups,
  isPending,
  onClose,
  onSubmit,
}: {
  open: boolean;
  groups: { id: string; name: string }[];
  isPending: boolean;
  onClose: () => void;
  onSubmit: (input: { toGroupId: string; reason: string; notes?: string }) => void;
}) {
  const [toGroupId, setToGroupId] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <Modal open={open} onClose={onClose} title="Escalate ticket" size="sm">
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          if (!toGroupId || !reason.trim()) return;
          onSubmit({ toGroupId, reason: reason.trim(), ...(notes.trim() ? { notes: notes.trim() } : {}) });
        }}
        className="space-y-4"
      >
        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Escalate to group</span>
          <select
            aria-label="Escalation target group"
            value={toGroupId}
            onChange={(e) => {
              setToGroupId(e.target.value);
            }}
            className={SELECT_CLS}
            required
          >
            <option value="">Choose a group…</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Reason</span>
          <textarea
            aria-label="Escalation reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            rows={2}
            required
            placeholder="Why is this being escalated?"
            className={TEXTAREA_CLS}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Notes (optional)</span>
          <textarea
            aria-label="Escalation notes"
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
            }}
            rows={2}
            placeholder="Extra context for the receiving group"
            className={TEXTAREA_CLS}
          />
        </label>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isPending} disabled={!toGroupId || !reason.trim()}>
            Escalate
          </Button>
        </div>
      </form>
    </Modal>
  );
}
